/* Copyright (c) 2020-present CloudIO Inc */

import { AnyColumn } from 'componentregistry';
import { Position, UpdateVisibleCellsProps, VisibleCells } from './types';
import { ColumnGroup } from '../useColumns';

function getDelta(
  e: React.WheelEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  wheelRef: React.MutableRefObject<{
    x: number;
    y: number;
  }>,
): { deltaY: number; deltaX: number } {
  let deltaY = 0;
  let deltaX = 0;
  if (e.type === 'touchstart') {
    const event = e as React.TouchEvent<HTMLDivElement>;
    if (event.touches.length) {
      const touch = event.touches[0];
      wheelRef.current.x = touch.clientX;
      wheelRef.current.y = touch.clientY;
    }
  } else if (e.type === 'touchmove') {
    const event = e as React.TouchEvent<HTMLDivElement>;
    if (event.changedTouches.length) {
      const touch = event.changedTouches[0];
      deltaY = wheelRef.current.y - touch.clientY;
      deltaX = wheelRef.current.x - touch.clientX;
      wheelRef.current.x = touch.clientX;
      wheelRef.current.y = touch.clientY;
    }
  } else if (e.type === 'touchend') {
    wheelRef.current.x = -1;
    wheelRef.current.y = -1;
  } else {
    ({ deltaY, deltaX } = e as React.WheelEvent<HTMLDivElement>);
  }
  return { deltaY, deltaX };
}

export const adjustXYOnWheel = (
  e: React.WheelEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  position: Position,
  wheelRef: React.MutableRefObject<{
    x: number;
    y: number;
  }>,
): { top: number; left: number } => {
  let { deltaY, deltaX } = getDelta(e, wheelRef);
  if (Math.abs(deltaY) > Math.abs(deltaX)) {
    deltaX = 0;
  } else {
    deltaY = 0;
  }
  const { scrollHeight, bodyHeight, bodyWidth, scrollWidth } = position;
  let { left, top } = position;
  left += deltaX;
  left = Math.max(Math.min(scrollWidth - bodyWidth, left), 0);
  top += deltaY;
  top = Math.max(Math.min(scrollHeight - bodyHeight, top), 0);

  return { top, left };
};

export const adjustPosition = (
  e: React.WheelEvent<HTMLDivElement>,
  position: Position,
): Position => {
  let { deltaY, deltaX } = e;
  if (Math.abs(deltaY) > Math.abs(deltaX)) {
    deltaX = 0;
  } else {
    deltaY = 0;
  }
  const { scrollHeight, bodyHeight, bodyWidth, scrollWidth } = position;
  let { left, top } = position;
  left += deltaX;
  left = Math.min(scrollWidth - bodyWidth, left);
  left = Math.max(left, 0);
  top += deltaY;
  top = Math.min(scrollHeight - bodyHeight, top);
  top = Math.max(Math.round(top), 0);

  return { ...position, top, left };
};

export const updateVisibleCells = ({
  columnWidths,
  fixedLeftColumns,
  fixedRightColumns,
  footerRows,
  headerRows,
  position,
  rowHeight,
  totalColumns,
  totalRows,
  rowBuffer,
  columnBuffer,
  previousVisibleCells,
}: UpdateVisibleCellsProps): VisibleCells => {
  let endColumn = totalColumns - 1;
  let startColumn = 0;
  const { top, left, bodyWidth, bodyHeight } = position;
  let w = 0;
  for (let i = fixedLeftColumns; i < totalColumns - fixedRightColumns; i++) {
    w += columnWidths[i];
    if (w >= left) {
      startColumn = i;
      break;
    }
  }
  const right = left + bodyWidth;
  for (let i = startColumn; i < totalColumns - fixedRightColumns; i++) {
    if (w >= right || i === totalColumns - fixedRightColumns - 1) {
      endColumn = i;
      break;
    }
    w += columnWidths[i + 1];
  }
  const result = {
    endColumn: Math.min(endColumn, totalColumns - fixedRightColumns - 1),
    endRow: Math.min(
      totalRows - footerRows - 1,
      headerRows + Math.ceil((top + bodyHeight) / rowHeight) - 1,
    ),
    startColumn: Math.max(fixedLeftColumns, startColumn),
    startRow: Math.max(headerRows, headerRows + Math.floor(top / rowHeight)),
  };
  let { startRow, endRow, startColumn: startCol, endColumn: endCol } = result;
  const actualEndRow = endRow;
  const actualStartRow = startRow;

  if (previousVisibleCells.endRow && previousVisibleCells.endColumn) {
    const _startRow = Math.max(headerRows, startRow - 10);
    const _endRow = Math.min(totalRows - footerRows - 1, endRow + 10);
    const _startCol = Math.max(fixedLeftColumns, startCol - 5);
    const _endCol = Math.min(totalColumns - fixedRightColumns - 1, endCol + 5);
    if (
      previousVisibleCells.startRow <= _startRow &&
      previousVisibleCells.endRow >= _endRow &&
      previousVisibleCells.startColumn <= _startCol &&
      previousVisibleCells.endColumn >= _endCol &&
      previousVisibleCells.totalRows === totalRows
    ) {
      previousVisibleCells.actualEndRow = actualEndRow;
      previousVisibleCells.actualStartRow = actualStartRow;
      return previousVisibleCells;
    }
  }

  if (rowBuffer > 0) {
    const visibleRows = endRow - startRow + 1;
    startRow = Math.max(
      headerRows,
      Math.floor(startRow - visibleRows * rowBuffer),
    );
    endRow = Math.min(
      totalRows - footerRows - 1,
      Math.floor(endRow + visibleRows * rowBuffer),
    );
  }
  if (columnBuffer > 0) {
    const visibleColumns = endCol - startCol + 1;
    startCol = Math.max(
      fixedLeftColumns,
      Math.floor(startCol - visibleColumns * columnBuffer),
    );
    endCol = Math.min(
      totalColumns - fixedRightColumns - 1,
      Math.floor(endCol + visibleColumns * columnBuffer),
    );
  }

  previousVisibleCells.startRow = startRow;
  previousVisibleCells.endRow = endRow;
  previousVisibleCells.startColumn = startCol;
  previousVisibleCells.endColumn = endCol;
  previousVisibleCells.totalRows = totalRows;
  previousVisibleCells.actualStartRow = actualStartRow;
  previousVisibleCells.actualEndRow = actualEndRow;

  return previousVisibleCells;
};

export const makePosition = (ele: HTMLElement, isRTL: boolean): Position => {
  const { scrollLeft } = ele;
  const left = scrollLeft;
  return {
    bodyHeight: ele.clientHeight,
    left,
    scrollHeight: ele.scrollHeight,
    scrollWidth: ele.scrollWidth,
    top: ele.scrollTop,
    bodyWidth: ele.clientWidth,
  };
};

export function makeColumnWidths(
  width: number,
  totalColumns: number,
  columnWidth: (index: number) => number,
  columns: AnyColumn[],
  vertialScrollbarSize: number,
  expandLastColumn?: boolean,
  autoFitColumns?: boolean,
  columnGroups?: ColumnGroup[][],
  actualColumns?: AnyColumn[],
) {
  let innerWidth = 0;
  const columnWidths: number[] = [];
  const columnLefts: number[] = [];

  const getColumnGroupWidth = (column: AnyColumn) => {
    let _width = 0;
    columnGroups?.forEach((cg) => {
      cg.forEach((_cg) => {
        if (_cg.itemIds.includes(column.itemId)) {
          _cg.itemIds.forEach((v) => {
            actualColumns?.forEach((c, i) => {
              if (c.itemId === v) {
                _width = _width + columnWidth(i);
              }
            });
          }, 0);
        }
      });
    });
    return _width || column.props.width || 200;
  };

  if (autoFitColumns) {
    const totalColumnWidth = columns.reduce(
      (sum, column, index) => sum + columnWidth(index),
      0,
    );
    const availableWidth = width - vertialScrollbarSize;
    let totalWidthDistributed = 0;
    columns.forEach((_column, index) => {
      const currentColumnWidth = columnWidth(index);
      const newWidth = (currentColumnWidth / totalColumnWidth) * availableWidth;
      columnWidths[index] = newWidth;
      totalWidthDistributed += newWidth;
    });

    const totalDistributedWidth = totalWidthDistributed + vertialScrollbarSize;
    if (totalDistributedWidth < width && expandLastColumn) {
      const balance = width - totalDistributedWidth;
      columnWidths[columnWidths.length - 1] += balance;
    }
    let columnLeft = 0;
    columnWidths.forEach((w, index) => {
      columnLefts[index] = columnLeft;
      columnLeft += w;
    });

    return { columnWidths, columnLefts, innerWidth: width };
  }

  for (let i = 0; i < totalColumns; i++) {
    const w = columnGroups ? getColumnGroupWidth(columns[i]) : columnWidth(i);
    columnWidths[i] = w;
    columnLefts[i] = innerWidth;
    innerWidth += w;
  }

  if (innerWidth < width) {
    let balance = width - innerWidth - vertialScrollbarSize;
    if (columns) {
      const totalFlex = columns.reduce((f, c) => f + (c.props.flex ?? 0), 0);
      if (totalFlex > 0) {
        const widthPerFlex = balance / totalFlex;
        balance = 0;
        let flex = 0;
        let w = 0;
        innerWidth = 0;
        for (let i = 0; i < totalColumns; i++) {
          flex = columns[i].props.flex ?? 0;
          w = columnWidths[i];
          if (flex) {
            w += widthPerFlex * flex;
            columnWidths[i] = w;
          }
          columnLefts[i] = innerWidth;
          innerWidth += w;
        }
      }
    }
    if (balance > 0 && expandLastColumn) {
      columnWidths[columnWidths.length - 1] += balance;
    }
    innerWidth = width;
  }

  return { columnWidths, columnLefts, innerWidth };
}

export function getScrollbarSize() {
  return 12;
}
